<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'CreateProductModal',
  props: {
    editMode: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        product_name: '',
        description: '',
        quantity: '',
        pricing: [
          {
            currency: 'MYR',
            cost: null,
            per_value: 1,
            per_unit: 'day'
          }
        ],
        image: null,
      },
      errors: {},
      isSubmitting: false,
      submitError: '',
      imagePreview: null,
    };
  },
  watch: {
    product: {
      handler(newVal) {
        if (this.editMode && newVal) {
          this.form.product_name = newVal.product_name || '';
          this.form.description = newVal.description || '';
          this.form.quantity = newVal.quantity || '';
          this.imagePreview = newVal.presigned_image_url || null;
          this.form.image = null;
          // Handle multiple pricing
          if (newVal.pricing) {
            let pricingArr = [];
            if (typeof newVal.pricing === 'string') {
              try {
                const parsed = JSON.parse(newVal.pricing);
                pricingArr = Array.isArray(parsed) ? parsed : [parsed];
              } catch (e) {
                console.error('Invalid pricing JSON:', newVal.pricing);
              }
            } else if (Array.isArray(newVal.pricing)) {
              pricingArr = newVal.pricing;
            }
            if (pricingArr.length > 0) {
              this.form.pricing = pricingArr.map(p => ({
                currency: p.currency || 'MYR',
                cost: p.cost ?? null,
                per_value: p.per_value ?? 1,
                per_unit: p.per_unit || 'day'
              }));
            } else {
              this.form.pricing = [
                {
                  currency: 'MYR',
                  cost: null,
                  per_value: 1,
                  per_unit: 'day'
                }
              ];
            }
          } else {
            this.form.pricing = [
              {
                currency: 'MYR',
                cost: null,
                per_value: 1,
                per_unit: 'day'
              }
            ];
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('rentalBranch', ['createProduct', 'updateProduct']),
    validate() {
      this.errors = {};
      
      if (!this.form.product_name) {
        this.errors.product_name = 'Product name is required.';
      }

      if (!this.form.quantity || isNaN(Number(this.form.quantity)) || Number(this.form.quantity) < 0) {
        this.errors.quantity = 'Quantity is required and must be a non-negative number.';
      }

      let hasCompletePricingRow = false;
      this.form.pricing.forEach((p, idx) => {
        const allFilled = p.currency && p.cost && p.per_value && p.per_unit;
        const anyFilled = p.currency || p.cost || p.per_value || p.per_unit;

        if (allFilled) {
          hasCompletePricingRow = true;
        } else if (anyFilled) {
          if (!this.errors.pricing) this.errors.pricing = {};
          this.errors.pricing[idx] = 'All fields are required for a pricing row.';
        }
      });

      if (!hasCompletePricingRow) {
        if (!this.errors.pricing) this.errors.pricing = {};
        this.errors.pricing._base = 'At least one complete pricing row is required.';
      }

      return Object.keys(this.errors).length === 0;
    },
    addPricingRow() {
      this.form.pricing.push({ currency: null, cost: null, per_value: 1, per_unit: null });
    },
    removePricingRow(idx) {
      if (this.form.pricing.length > 1) {
        this.form.pricing.splice(idx, 1);
      }
    },
    handleImageChange(e) {
      const file = e.target.files[0];
      this.form.image = file;
      if (file) {
        const reader = new FileReader();
        reader.onload = e2 => {
          this.imagePreview = e2.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = this.product && this.product.presigned_image_url ? this.product.presigned_image_url : null;
      }
    },
    async handleSubmit(e) {
      e.preventDefault();
      if (!this.validate()) return;
      this.isSubmitting = true;
      this.submitError = '';
      useAlert('Submitting...');
      try {
        const branch_id = this.$route.params.branchId;
        const formData = new FormData();
        formData.append('branch_id', branch_id);
        formData.append('product_name', this.form.product_name);
        formData.append('quantity', this.form.quantity);
        if (this.form.description) {
          formData.append('description', this.form.description);
        }
        // Pricing: always send as JSON string array, filter out incomplete rows
        const pricingArr = this.form.pricing
          .filter(p => p.currency && p.cost && p.per_value && p.per_unit)
          .map(p => ({
            currency: p.currency,
            cost: Number(p.cost),
            per_value: Number(p.per_value),
            per_unit: p.per_unit
          }));
        if (pricingArr.length > 0) {
          formData.append('pricing', JSON.stringify(pricingArr));
        }
        if (this.form.image) {
          formData.append('image', this.form.image);
        }
        if (this.editMode && this.product) {
          await this.updateProduct({ product_id: this.product.product_id, payload: formData });
          useAlert('Product updated successfully');
          this.$emit('updated');
        } else {
          await this.createProduct({ payload: formData });
          useAlert('Product created successfully');
          this.$emit('created');
        }
      } catch (err) {
        this.submitError = err.message || (this.editMode ? 'Failed to update product.' : 'Failed to create product.');
        useAlert(this.submitError);
      } finally {
        this.isSubmitting = false;
      }
    },
    handleClose() {
      this.$emit('close');
    }
  }
};
</script>

<template>
  <div class="z-50 fixed inset-0 flex items-center bg-slate-800/40 px-4">
    <div class="max-h-[80vh] overflow-y-auto mx-auto my-2 p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-xl min-w-[350px] w-full max-w-md relative">
      <button @click="handleClose" class="absolute top-2 right-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl">&times;</button>
      <h2 class="text-xl font-semibold mb-4 text-slate-900 dark:text-slate-100">{{ editMode ? 'Edit Product' : 'Create Product' }}</h2>
      <form id="productForm" @submit="handleSubmit" autocomplete="off" class="flex flex-col gap-3">
        <div>
          <label class="block text-sm font-medium mb-1">Product Name<span class="text-red-500">*</span></label>
          <input v-model="form.product_name" type="text" class="reset-base w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.product_name}" />
          <span v-if="errors.product_name" class="text-xs text-red-500">{{ errors.product_name }}</span>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Description</label>
          <textarea v-model="form.description" rows="3" class="w-full px-3 py-2 border rounded focus:outline-none focus:ring"></textarea>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Quantity<span class="text-red-500">*</span></label>
          <input v-model="form.quantity" type="number" min="0" class="reset-base w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.quantity}" />
          <span v-if="errors.quantity" class="text-xs text-red-500">{{ errors.quantity }}</span>
        </div>
        <div class="mb-6">
          <details 
            class="border rounded" 
            :class="{
              'border-red-500': errors.pricing && errors.pricing._base,
              'border-slate-200': !(errors.pricing && errors.pricing._base)
            }" open>
            <summary class="cursor-pointer px-4 py-3 bg-gray-50 rounded-t">
              Add Pricing
            </summary>
            <div class="p-4 space-y-4">
              <div v-for="(pricing, idx) in form.pricing" :key="idx" class="flex flex-col gap-2 border-b pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0">
                <div class="flex gap-2 items-center">
                  <select
                    v-model="pricing.currency"
                    class="px-2 py-2 border-r rounded-l focus:outline-none focus:ring"
                  >
                    <option disabled :value="null">Currency</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="SGD">SGD</option>
                    <option value="MYR">MYR</option>
                    <option value="JPY">JPY</option>
                    <option value="CNY">CNY</option>
                    <option value="AUD">AUD</option>
                    <option value="CAD">CAD</option>
                    <option value="INR">INR</option>
                  </select>
                  <input
                    v-model.number="pricing.cost"
                    type="number" step="0.01" min="0"
                    placeholder="Cost"
                    class="px-3 py-2 border rounded-r focus:outline-none focus:ring"
                  />
                  <input
                    v-model.number="pricing.per_value"
                    type="number" step="1" min="1"
                    placeholder="Per value"
                    class="w-16 px-2 py-2 border rounded focus:outline-none focus:ring"
                  />
                  <select
                    v-model="pricing.per_unit"
                    class="px-2 py-2 border rounded focus:outline-none focus:ring"
                  >
                    <option disabled :value="null">unit</option>
                    <option value="day">day</option>
                  </select>
                  <button type="button" @click="removePricingRow(idx)" :disabled="form.pricing.length === 1" class="ml-2 px-2 py-1 text-xs rounded bg-red-100 text-red-600 hover:bg-red-200" v-if="form.pricing.length > 1">Remove</button>
                </div>
                <span v-if="errors.pricing && errors.pricing[idx]" class="text-xs text-red-500">{{ errors.pricing[idx] }}</span>
              </div>
              <button type="button" @click="addPricingRow" class="mt-2 px-3 py-1 rounded bg-woot-100 text-woot-700 hover:bg-woot-200 text-sm">Add Pricing</button>
            </div>
          </details>
          <span v-if="errors.pricing && errors.pricing._base" class="text-xs text-red-500">{{ errors.pricing._base }}</span>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Image</label>
          <input type="file" accept="image/*" @change="handleImageChange" class="w-full" />
          <div v-if="imagePreview" class="mt-2">
            <img :src="imagePreview" alt="Preview" class="max-h-32 rounded border" />
          </div>
        </div>
        <div v-if="submitError" class="text-xs text-red-500">{{ submitError }}</div>
        <div class="flex gap-2 mt-2">
          <button type="button" @click="handleClose" class="flex-1 py-2 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600">Cancel</button>
          <button type="submit" :disabled="isSubmitting || !validate()" class="flex-1 py-2 rounded bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-60">{{ isSubmitting ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Product' : 'Create Product') }}</button>
        </div>
      </form>
    </div>
  </div>
</template>